/********************************/
/*      SPIRAL UTILITIES        */
/********************************/

/**
 * Spiral class - pre-calculates spiral positions
 */
export class Spiral {
  constructor(numPoints, maxRadius, spiralTurns) {
    this.numPoints = numPoints;
    this.maxRadius = maxRadius;
    this.spiralTurns = spiralTurns;

    // Pre-calculate positions for each point on the spiral
    this.points = [];
    this.preCalculate();
  }

  preCalculate() {
    for (let i = 0; i < this.numPoints; i++) {
      const t = i / this.numPoints; // Normalized position along spiral (0-1)

      // Calculate spiral position at base angle (spinSpeed = 0)
      const baseAngle = t * this.spiralTurns * TWO_PI;
      const radius = this.maxRadius * (1 - t); // Radius decreases toward center

      this.points.push({
        t: t,
        baseAngle: baseAngle,
        radius: radius
      });
    }
  }

  getPoint(index, spinOffset = 0) {
    const point = this.points[index];
    const angle = point.baseAngle - spinOffset;

    return {
      x: cos(angle) * point.radius,
      y: sin(angle) * point.radius,
      t: point.t,
      radius: point.radius,
      angle: angle
    };
  }
}

/**
 * SpiralPoint class - represents a point on a spiral with optional properties
 * Handles position calculation but leaves rendering to the caller
 */
export class SpiralPoint {
  constructor(spiral, index, properties = {}) {
    this.spiral = spiral;
    this.index = index;

    // Store custom properties (e.g., size, opacity, rotation offset)
    this.properties = properties;

    // Random angle offset for variety
    this.angleOffset = noise(index * 999) * TWO_PI * 0.3;
  }

  /**
   * Get the current position and properties for this point
   * @param {number} spinOffset - Global rotation offset for the spiral
   * @returns {object} Position and properties for rendering
   */
  getState(spinOffset = 0) {
    const point = this.spiral.getPoint(this.index, spinOffset);

    return {
      x: point.x,
      y: point.y,
      t: point.t,
      radius: point.radius,
      angle: point.angle,
      angleOffset: this.angleOffset,
      ...this.properties
    };
  }
}
